import { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { Product, ProductGroup } from '../types';

interface ProductFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (product: Partial<Product>) => void;
  product?: Product;
  groups: ProductGroup[];
}

const ProductFormModal = ({
  isOpen,
  onClose,
  onSave,
  product,
  groups,
}: ProductFormModalProps) => {
  const [formData, setFormData] = useState<Partial<Product>>({
    name: '',
    group: '',
    unitPrice: 0,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (product) {
      // Map database field names to form field names
      setFormData({
        name: (product as any).productName || product.name || '',
        group: (product as any).groupName || product.group || '',
        unitPrice: product.unitPrice || 0,
      });
    } else {
      setFormData({
        name: '',
        group: '',
        unitPrice: 0,
      });
    }
    setErrors({});
  }, [product, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Product name is required';
    }

    if (!formData.group) {
      newErrors.group = 'Product group is required';
    }

    if (formData.unitPrice === undefined || formData.unitPrice < 0) {
      newErrors.unitPrice = 'Valid unit price is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // Map form field names back to database field names
      const productData = {
        productName: formData.name,
        groupName: formData.group,
        unitPrice: formData.unitPrice,
      };
      onSave(productData);
      onClose();
    }
  };

  const handleChange = (field: keyof Product, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-card rounded-lg shadow-xl max-w-lg w-full">
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="text-xl font-semibold text-text-primary">
              {product ? 'Edit Product' : 'Add Product'}
            </h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-muted rounded transition-colors"
              aria-label="Close modal"
            >
              <Icon name="X" size={20} className="text-text-secondary" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <Input
              label="Product"
              type="text"
              placeholder="Enter product name"
              value={formData.name || ''}
              onChange={(e) => handleChange('name', e.target.value)}
              error={errors.name}
              required
            />

            <Select
              label="Group"
              options={groups.map((g) => ({ value: g.name, label: g.name }))}
              value={formData.group || ''}
              onChange={(value) => handleChange('group', value)}
              error={errors.group}
              required
            />

            <Input
              label="Unit Price (€)"
              type="number"
              placeholder="0.00"
              step="0.01"
              value={formData.unitPrice || ''}
              onChange={(e) => handleChange('unitPrice', parseFloat(e.target.value) || 0)}
              error={errors.unitPrice}
              required
            />

            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <Button variant="outline" onClick={onClose} type="button">
                Cancel
              </Button>
              <Button type="submit">
                {product ? 'Update' : 'Add'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ProductFormModal;